import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import CardInfo from './CardInfo';

/**
 * FlightRisk — sortable tenant flight-risk table.
 * Reads `data.flightRisk[]` from /api/command/:concessionaireId.
 */
function severityFromScore(score) {
    if (score == null) return 'low';
    if (score >= 0.75) return 'critical';
    if (score >= 0.5) return 'high';
    if (score >= 0.25) return 'medium';
    return 'low';
}

function TrendArrow({ trend }) {
    if (trend === 'improving') return <TrendingUp size={14} className="v3-cc-trend-up" />;
    if (trend === 'declining') return <TrendingDown size={14} className="v3-cc-trend-down" />;
    return <Minus size={14} className="v3-cc-trend-flat" />;
}

export default function FlightRisk({ rows }) {
    const { t } = useTranslation();
    const [sortDir, setSortDir] = useState('desc');

    const sorted = useMemo(() => {
        const list = Array.isArray(rows) ? [...rows] : [];
        list.sort((a, b) => {
            const av = a.score ?? 0, bv = b.score ?? 0;
            return sortDir === 'desc' ? bv - av : av - bv;
        });
        return list;
    }, [rows, sortDir]);

    return (
        <div className="v3-card v3-cc-card">
            <div className="v3-cc-card-header-row">
                <h3>{t('Flight Risk')}</h3>
                <CardInfo title={t('About Flight Risk')}>
                    <p>
                        {t('This card ranks tenants by')} <strong>{t('flight risk')}</strong> — {t('the likelihood a tenant is struggling and may default or leave.')}
                    </p>
                    <p>
                        <strong>{t('Score')}</strong> {t('is a composite from 0 to 1, weighted: 40% OCR, 30% sales trend (3-month vs 6-month run-rate), 20% lease proximity, 10% zero-sales streaks. Higher = more at risk.')}
                    </p>
                    <p>
                        <strong>{t('OCR%')}</strong> {t('(Occupancy Cost Ratio) = (annual rent + charges) / annual net sales × 100. Above 25% is the danger zone.')}
                    </p>
                    <p>
                        <strong>{t('Trend')}</strong> {t('compares the recent 3-month sales run-rate against the 6-month run-rate. A down arrow means sales are decelerating.')}
                    </p>
                    <p>
                        <strong>{t('Top driver')}</strong> {t('is the single factor contributing most to that tenant’s score.')}
                    </p>
                </CardInfo>
            </div>
            {sorted.length === 0 ? (
                <div className="v3-text-muted" style={{ fontSize: 13 }}>{t('No flight-risk tenants flagged.')}</div>
            ) : (
                <div className="v3-cc-table-wrap">
                <table className="v3-cc-table">
                    <thead>
                        <tr>
                            <th>{t('Tenant')}</th>
                            <th
                                onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSortDir(d => d === 'desc' ? 'asc' : 'desc'); } }}
                                aria-sort={sortDir === 'desc' ? 'descending' : 'ascending'}
                            >
                                {t('Score')} {sortDir === 'desc' ? '▼' : '▲'}
                            </th>
                            <th>{t('OCR%')}</th>
                            <th>{t('Trend')}</th>
                            <th>{t('Top driver')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((r) => {
                            const sev = severityFromScore(r.score);
                            return (
                                <tr key={r.tenant_id}>
                                    <td>{r.tenant_name || '—'}</td>
                                    <td>
                                        <span className={`v3-cc-dot ${sev}`} aria-hidden="true" />
                                        <span className="visually-hidden">{t('{{sev}} severity', { sev })}, </span>
                                        {(r.score ?? 0).toFixed(2)}
                                    </td>
                                    <td>{r.ocr_pct != null ? `${(r.ocr_pct * 100).toFixed(1)}%` : '—'}</td>
                                    <td><TrendArrow trend={r.trend} /></td>
                                    <td className="v3-text-muted" style={{ fontSize: 11 }}>
                                        {Array.isArray(r.drivers) && r.drivers.length ? r.drivers[0] : '—'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>
            )}
        </div>
    );
}
